import React from 'react'
import { canvasFrame, palette, typography } from './pqrTheme'
import type { PqrPreviewTemplateData } from '../../../utils/pqrPreviewClient'
import type { PqrSectionName } from './pqrPreviewStaging'
import {
  classifyEquipmentNoteLabel,
  deriveEquipmentKeyFromWelderRole,
  type PqrFieldDescriptor,
  type PqrFieldLinkContext
} from './pqrFieldLinkRegistry'
import { WELDING_VARIABLE_ROW_DEFS } from '../../../utils/pqrWeldingVariableRowDefs'
import { resolveWeldingVariableNavigationPass } from '../../../utils/floorSheetMisfirePrefill'
import PqrPreviewFieldLink, {
  type PqrPreviewFieldLinkActions,
  usePqrFieldLinkNavigation
} from './PqrPreviewFieldLink'
import PqrNavigateConfirmModal from './PqrNavigateConfirmModal'

/**
 * Pixel-faithful HTML replica of `server/src/pdf/PqrTemplate.tsx`. Renders
 * the same eight regions in the same order, sourced from the same
 * `PqrTemplateData` payload the PDF consumes — guaranteeing the canvas and
 * the generated PDF stay structurally identical.
 *
 * Layout summary (single-page A3 portrait — mirrors PDF page model):
 *   TopBand + CompanyBlock + MaterialTable + MidBand
 *   (WeldingVariables + JointPreparation) + WeldingParametersBand
 *   (WeldingParameters + WeldPassSequence) + NotesBlock +
 *   WeldCompletionTimes + AccompanyingReports + Comments + SignatureFooter
 *
 * Editable regions remain click targets (anchor wrappers carry
 * `data-section` + `onClick`); the dirty affordance is a 4px amber left
 * strip painted via `box-shadow inset` on the wrapper. The visual table
 * markup itself is never altered by the dirty state, so it remains a
 * faithful PDF replica.
 */

export interface ProjectIdentitySummary {
  /** Project name shown in the page footer. */
  projectName: string
}

interface Props {
  /** PDF-template payload returned by `GET /reports/pqr-preview`. */
  data: PqrPreviewTemplateData
  /** Project-level summary used by the page footer. */
  project: ProjectIdentitySummary
  /** Per-section dirty predicate; used to paint the amber left strip. */
  isDirty: (section: PqrSectionName) => boolean
  /** Section click — parent opens the matching modal. */
  onSectionClick: (section: PqrSectionName) => void
  /**
   * Identity context for field-link navigation (slice 01 onwards).
   * Optional: when omitted, navigable rows fall back to section-click
   * behaviour so legacy callers / older tests keep working.
   */
  fieldLinkContext?: PqrFieldLinkContext
  /**
   * Staging-buffer actions for field-link navigation. When supplied the
   * confirm modal can negotiate unsaved edits via the three-button
   * variant; when absent the link assumes the buffer is always clean.
   */
  fieldLinkActions?: PqrPreviewFieldLinkActions
}

// ───────────────────────────── primitives ────────────────────────────

const HAIRLINE = `0.5px solid ${palette.border}`
const HAIRLINE_SOFT = `0.5px solid ${palette.borderHairline}`
const BORDER = `1px solid ${palette.border}`

const baseText: React.CSSProperties = {
  fontFamily: typography.family,
  color: palette.text,
  fontSize: typography.body
}

function SectionHeader({ title }: { title: string }) {
  // Matches `styles.sectionHeader` in the PDF: solid CRC-red band with
  // uppercase, bold, letter-spaced caps.
  return (
    <div
      style={{
        backgroundColor: palette.banner,
        color: '#ffffff',
        fontSize: typography.sectionHeader,
        fontWeight: 700,
        padding: '2px 4px',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        fontFamily: typography.family
      }}
    >
      {title}
    </div>
  )
}

function SectionBody({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        border: BORDER,
        borderTop: 'none',
        ...baseText
      }}
    >
      {children}
    </div>
  )
}

/**
 * Edit-target wrapper. Anchors the click handler, applies the amber dirty
 * strip via `box-shadow` (so it does not affect inner layout widths), and
 * carries the `data-section` attribute so tests can locate it deterministically.
 */
function EditTarget({
  section,
  isDirty,
  onClick,
  children,
  style
}: {
  section: PqrSectionName
  isDirty: boolean
  onClick: () => void
  children: React.ReactNode
  style?: React.CSSProperties
}) {
  return (
    <div
      data-section={section}
      data-dirty={isDirty ? 'true' : 'false'}
      role="button"
      tabIndex={0}
      aria-label={`Edit ${section}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      style={{
        position: 'relative',
        cursor: 'pointer',
        boxShadow: isDirty ? `inset 4px 0 0 0 ${palette.dirty}` : 'none',
        backgroundColor: isDirty ? palette.dirtySurface : 'transparent',
        ...style
      }}
    >
      {children}
    </div>
  )
}

// ──────────────────────────── helpers ────────────────────────────────

function formatGeneratedAt(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(
    d.getUTCHours()
  )}:${pad(d.getUTCMinutes())}`
}

// ──────────────────────────── regions ────────────────────────────────

function TopBand({
  data,
  project: _project,
  isDirty,
  onSectionClick
}: {
  data: PqrPreviewTemplateData
  project: ProjectIdentitySummary
  isDirty: (s: PqrSectionName) => boolean
  onSectionClick: (s: PqrSectionName) => void
}) {
  const dirty = isDirty('identifiers')
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        borderBottom: `2px solid ${palette.banner}`,
        paddingBottom: 4,
        marginBottom: 4,
        gap: 8
      }}
    >
      <div
        style={{
          width: 140,
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
      >
        <img
          src="/crc-logo.png"
          alt="CRC Evans"
          style={{ width: 140, height: 56, objectFit: 'contain' }}
        />
      </div>
      <div style={{ flexGrow: 1, textAlign: 'center' }}>
        <p
          style={{
            ...baseText,
            fontSize: typography.title,
            fontWeight: 700,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            color: palette.text,
            margin: 0
          }}
        >
          Procedure Qualification Record
        </p>
      </div>
      <EditTarget
        section="identifiers"
        isDirty={dirty}
        onClick={() => onSectionClick('identifiers')}
        style={{
          minWidth: 160,
          border: HAIRLINE,
          borderLeft: `3px solid ${palette.banner}`,
          padding: 4
        }}
      >
        <TopInfoRow label="PQR No." value={data.identifiers.pqrNumber} />
        <TopInfoRow label="Rev." value={data.identifiers.revision} />
        <TopInfoRow label="Sheet" value={data.identifiers.sheetOf} />
      </EditTarget>
    </div>
  )
}

function TopInfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: typography.micro,
        padding: '0.5px 0',
        fontFamily: typography.family
      }}
    >
      <span style={{ color: palette.textMuted, fontWeight: 700 }}>{label}</span>
      <span style={{ color: palette.text, fontWeight: 700 }}>{value || '—'}</span>
    </div>
  )
}

function CompanyBlock({ data }: { data: PqrPreviewTemplateData }) {
  // Three rows mirror the PDF / XLSX rows 6-8:
  //   Company / Client         │ Industry Specification
  //   Contractor               │ Company Specification
  //   Project                  │ CRC Weld file No.
  const rows: Array<[[string, string], [string, string]]> = [
    [
      ['Company / Client:', data.company.company],
      ['Industry Specification:', data.company.industrySpec]
    ],
    [
      ['Contractor:', data.company.contractor],
      ['Company Specification:', data.company.companySpec]
    ],
    [
      ['Project:', data.company.project],
      ['CRC Weld file No.:', data.company.crcWeldFileNo]
    ]
  ]
  return (
    <div
      style={{
        border: HAIRLINE,
        borderLeft: `3px solid ${palette.banner}`,
        marginBottom: 4
      }}
    >
      {rows.map(([left, right], idx) => (
        <div
          key={idx}
          style={{
            display: 'flex',
            borderBottom: idx === rows.length - 1 ? 'none' : HAIRLINE_SOFT
          }}
        >
          <CompanyCell label={left[0]} value={left[1]} bordered />
          <CompanyCell label={right[0]} value={right[1]} />
        </div>
      ))}
    </div>
  )
}

function CompanyCell({
  label,
  value,
  bordered
}: {
  label: string
  value: string
  bordered?: boolean
}) {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        padding: '2px 4px',
        borderRight: bordered ? HAIRLINE_SOFT : 'none'
      }}
    >
      <span
        style={{
          width: 130,
          fontSize: typography.caption,
          fontWeight: 700,
          color: palette.textMuted
        }}
      >
        {label}
      </span>
      <span
        style={{
          flex: 1,
          fontSize: typography.body,
          color: palette.text
        }}
      >
        {value || '\u00A0'}
      </span>
    </div>
  )
}

// ----- Material table ----------------------------------------------------

function MaterialTable({
  data,
  isDirty,
  onSectionClick,
  fieldLinkContext,
  fieldLinkActions
}: {
  data: PqrPreviewTemplateData
  isDirty: (s: PqrSectionName) => boolean
  onSectionClick: (s: PqrSectionName) => void
  fieldLinkContext?: PqrFieldLinkContext
  fieldLinkActions?: PqrPreviewFieldLinkActions
}) {
  // Materials no longer has a staging slot (Slice 05 of
  // pqr-preview-field-navigation deleted it). The section-level
  // EditTarget is gone; per-row clicks navigate to WpsBaseMaterial.
  void onSectionClick
  void isDirty
  const cols: Array<{
    key: keyof PqrPreviewTemplateData['materials'][number]
    header: string
    width?: number
    flex?: number
  }> = [
    { key: 'label', header: '', width: 56 },
    { key: 'materialSpec', header: 'Material Specification', flex: 1 },
    { key: 'typeOrGrade', header: 'Type or Grade', flex: 1 },
    { key: 'nominalThicknessIn', header: 'Nom. Thickness (in)', flex: 1 },
    { key: 'nominalDiameterIn', header: 'Nom. Diameter (in)', flex: 1 },
    { key: 'manufacturer', header: 'Manufacturer(s)', flex: 1 },
    { key: 'heatNo', header: 'Heat No.', flex: 1 }
  ]
  return (
    <div data-section="materials" style={{ marginBottom: 4 }}>
      <SectionHeader title="Material" />
      <SectionBody>
        <table
          role="table"
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            tableLayout: 'fixed'
          }}
        >
          <colgroup>
            {cols.map((c) => (
              <col
                key={c.key as string}
                style={c.width ? { width: c.width } : undefined}
              />
            ))}
          </colgroup>
          <thead>
            <tr style={{ backgroundColor: palette.banner }}>
              {cols.map((c) => (
                <th
                  key={c.key as string}
                  style={{
                    color: '#ffffff',
                    fontSize: typography.caption,
                    fontWeight: 700,
                    padding: '2px 3px',
                    borderRight: `0.5px solid ${palette.bannerDeep}`,
                    textAlign: 'center'
                  }}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.materials.length === 0 ? (
              <tr>
                <td
                  colSpan={cols.length}
                  style={{
                    padding: '4px 6px',
                    color: palette.textMuted,
                    fontSize: typography.body
                  }}
                >
                  —
                </td>
              </tr>
            ) : (
              data.materials.map((row, idx) => (
                <MaterialRow
                  key={`${row.label}-${idx}`}
                  row={row}
                  pipeIndex={idx}
                  cols={cols}
                  fieldLinkContext={fieldLinkContext}
                  fieldLinkActions={fieldLinkActions}
                />
              ))
            )}
          </tbody>
        </table>
      </SectionBody>
    </div>
  )
}

function MaterialRow({
  row,
  pipeIndex,
  cols,
  fieldLinkContext,
  fieldLinkActions
}: {
  row: PqrPreviewTemplateData['materials'][number]
  pipeIndex: number
  cols: Array<{
    key: keyof PqrPreviewTemplateData['materials'][number]
    header: string
    width?: number
    flex?: number
  }>
  fieldLinkContext?: PqrFieldLinkContext
  fieldLinkActions?: PqrPreviewFieldLinkActions
}) {
  const nav = usePqrFieldLinkNavigation(
    { section: 'materials', pipeIndex },
    `Material — ${row.label || `Pipe ${pipeIndex + 1}`}`,
    fieldLinkContext ?? {
      projectId: '',
      pipeSetId: '',
      weldId: null,
      syntheticWeldId: null,
      previewUrl: ''
    },
    fieldLinkActions
  )
  const clickable = !!fieldLinkContext && !nav.disabled

  return (
    <>
      <tr
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-label={clickable ? `Edit ${row.label || `Pipe ${pipeIndex + 1}`}` : undefined}
        onClick={clickable ? nav.handleClick : undefined}
        onKeyDown={clickable ? nav.handleKeyDown : undefined}
        style={{
          borderBottom: HAIRLINE,
          cursor: clickable ? 'pointer' : 'default'
        }}
      >
        {cols.map((col) => {
          const isLabel = col.key === 'label'
          return (
            <td
              key={col.key as string}
              style={{
                padding: '2px 3px',
                borderRight: HAIRLINE,
                fontSize: typography.body,
                color: palette.text,
                textAlign: isLabel ? 'left' : 'center',
                fontWeight: isLabel ? 700 : 400
              }}
            >
              {row[col.key]}
            </td>
          )
        })}
      </tr>
      {clickable ? (
        <PqrNavigateConfirmModal open={nav.open} {...nav.modalProps} />
      ) : null}
    </>
  )
}

// ----- Welding Variables + Joint Preparation (mid band) -----------------

function WeldingVariableCell({
  v,
  passLabel,
  rowKey,
  rowLabel,
  bevelIndex,
  fieldLinkContext,
  fieldLinkActions
}: {
  v: string
  passLabel: string
  rowKey?: string
  rowLabel: string
  bevelIndex: number
  fieldLinkContext?: PqrFieldLinkContext
  fieldLinkActions?: PqrPreviewFieldLinkActions
}) {
  // Cells can be navigable only when we know the (bevel, pass, rowKey)
  // triple AND the canvas is running with a field-link context. When the
  // pass list is the synthetic "—" placeholder (i.e. no passes captured
  // yet) the cell stays read-only because there's nowhere meaningful to
  // navigate to.
  const navigable = !!fieldLinkContext && !!rowKey && passLabel !== '—'
  const navigationPass = resolveWeldingVariableNavigationPass(passLabel)
  const ctxArg = fieldLinkContext ?? {
    projectId: '',
    pipeSetId: '',
    weldId: null,
    syntheticWeldId: null,
    previewUrl: ''
  }
  const descriptor: PqrFieldDescriptor = {
    section: 'welding-variable-cell',
    bevelIndex,
    passLabel: navigationPass,
    rowKey: rowKey ?? ''
  }
  const nav = usePqrFieldLinkNavigation(
    descriptor,
    `${rowLabel} — ${passLabel}`,
    ctxArg,
    fieldLinkActions
  )
  const clickable = navigable && !nav.disabled
  return (
    <>
      <td
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-label={clickable ? `Edit ${rowLabel} for ${passLabel}` : undefined}
        onClick={clickable ? nav.handleClick : undefined}
        onKeyDown={clickable ? nav.handleKeyDown : undefined}
        style={{
          fontSize: typography.body,
          padding: '2px 3px',
          borderRight: HAIRLINE,
          color: palette.text,
          textAlign: 'center',
          cursor: clickable ? 'pointer' : 'default'
        }}
      >
        {v}
      </td>
      {clickable ? <PqrNavigateConfirmModal open={nav.open} {...nav.modalProps} /> : null}
    </>
  )
}

function WeldingVariablesTable({
  data,
  isDirty,
  onSectionClick,
  fieldLinkContext,
  fieldLinkActions
}: {
  data: PqrPreviewTemplateData
  isDirty: (s: PqrSectionName) => boolean
  onSectionClick: (s: PqrSectionName) => void
  fieldLinkContext?: PqrFieldLinkContext
  fieldLinkActions?: PqrPreviewFieldLinkActions
}) {
  // Slice 07 of pqr-preview-field-navigation: Welding Variables is no
  // longer a staged section. Per-cell clicks navigate to WpsWeldDetails
  // directly via `WeldingVariableCell`, which is wired up here with the
  // field-link context (when one is available — e.g. the live PQR
  // Preview page) or rendered read-only (e.g. canvas snapshot tests
  // that have no router).
  void isDirty
  void onSectionClick
  const passes = data.weldingVariables.passes.length > 0
    ? data.weldingVariables.passes
    : ['—']
  return (
    <div
      data-section="weldingVariables"
      data-dirty="false"
      style={{ position: 'relative' }}
    >
      <SectionHeader title="Welding Variables" />
      <SectionBody>
        <WeldingVariablesTableInner
          data={data}
          passes={passes}
          fieldLinkContext={fieldLinkContext}
          fieldLinkActions={fieldLinkActions}
        />
      </SectionBody>
    </div>
  )
}

function WeldingVariablesTableInner({
  data,
  passes,
  fieldLinkContext,
  fieldLinkActions
}: {
  data: PqrPreviewTemplateData
  passes: string[]
  fieldLinkContext?: PqrFieldLinkContext
  fieldLinkActions?: PqrPreviewFieldLinkActions
}) {
  const bevelIndex = 0
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
      <colgroup>
        <col style={{ width: 130 }} />
        {passes.map((p) => (
          <col key={p} />
        ))}
      </colgroup>
      <thead>
        <tr style={{ backgroundColor: palette.banner }}>
          <th
            style={{
              color: '#ffffff',
              fontSize: typography.caption,
              fontWeight: 700,
              padding: '2px 3px',
              borderRight: `0.5px solid ${palette.bannerDeep}`,
              textAlign: 'left'
            }}
          >
            Variable
          </th>
          {passes.map((p) => (
            <th
              key={p}
              style={{
                color: '#ffffff',
                fontSize: typography.caption,
                fontWeight: 700,
                padding: '2px 3px',
                borderRight: `0.5px solid ${palette.bannerDeep}`,
                textAlign: 'center'
              }}
            >
              {p}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.weldingVariables.rows.length === 0 ? (
          <tr>
            <td
              colSpan={passes.length + 1}
              style={{
                padding: '4px 6px',
                color: palette.textMuted,
                fontSize: typography.body
              }}
            >
              No welding variables captured.
            </td>
          </tr>
        ) : (
          data.weldingVariables.rows.map((row) => {
            // Look up the canonical row key from the shared definitions.
            // The PDF/PqrPreview server template only carries the human
            // label down, so we recover the key by label-match here.
            const def = WELDING_VARIABLE_ROW_DEFS.find((d) => d.label === row.label)
            const values = row.values.length > 0 ? row.values : passes.map(() => '—')
            return (
              <tr key={row.label} style={{ borderBottom: HAIRLINE }}>
                <th
                  scope="row"
                  style={{
                    padding: '1.5px 3px',
                    borderRight: HAIRLINE,
                    textAlign: 'left',
                    fontSize: typography.caption,
                    fontWeight: 700,
                    color: palette.text
                  }}
                >
                  {row.label}
                  {row.unit ? (
                    <span
                      style={{
                        marginLeft: 3,
                        fontSize: typography.micro,
                        color: palette.textSubtle,
                        fontStyle: 'italic',
                        fontWeight: 400
                      }}
                    >
                      {row.unit}
                    </span>
                  ) : null}
                </th>
                {values.map((v, i) => (
                  <WeldingVariableCell
                    key={i}
                    v={v}
                    passLabel={passes[i]}
                    rowKey={def?.rowKey}
                    rowLabel={row.label}
                    bevelIndex={bevelIndex}
                    fieldLinkContext={fieldLinkContext}
                    fieldLinkActions={fieldLinkActions}
                  />
                ))}
              </tr>
            )
          })
        )}
      </tbody>
    </table>
  )
}

function BevelDiagram() {
  // Mirrors `BevelDiagram` in `server/src/pdf/PqrTemplate.tsx` (single-V
  // bevel reference sketch, no A-G overlays).
  return (
    <svg width={210} height={90} viewBox="0 0 210 90" aria-hidden>
      <path
        d="M5 45 L88 45 L100 82 L110 82 L122 45 L205 45 L205 68 L5 68 Z"
        stroke={palette.banner}
        strokeWidth={1.4}
        fill="none"
      />
      <path d="M100 82 L110 82" stroke={palette.text} strokeWidth={1} />
      <path
        d="M105 35 L105 88"
        stroke={palette.textSubtle}
        strokeWidth={0.5}
        strokeDasharray="2 2"
      />
    </svg>
  )
}

function JointPreparationPanelBody({
  jointType,
  dimensions,
  imageUrl,
  seamLocations
}: {
  jointType: string
  dimensions: PqrPreviewTemplateData['jointPreparation']['dimensions']
  imageUrl?: string
  seamLocations?: { upstream: string; downstream: string }
}) {
  return (
    <>
      <SectionHeader title="Joint Preparation" />
      <SectionBody>
        <div style={{ paddingTop: 4 }}>
          {jointType ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 4,
                padding: '2px 4px',
                borderBottom: HAIRLINE_SOFT,
                backgroundColor: palette.surfaceMuted
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  fontSize: typography.caption,
                  color: palette.textMuted,
                  fontWeight: 700
                }}
              >
                Joint Type:
              </span>
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: typography.body,
                  color: palette.text,
                  fontWeight: 700,
                  wordBreak: 'break-word'
                }}
              >
                {jointType}
              </span>
            </div>
          ) : null}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 4,
              borderBottom: HAIRLINE_SOFT
            }}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={jointType ? `${jointType} bevel diagram` : 'Bevel diagram'}
                style={{ maxWidth: 220, maxHeight: 110, objectFit: 'contain' }}
              />
            ) : (
              <BevelDiagram />
            )}
          </div>
          <div style={{ padding: 4 }} data-testid="pqr-geometry-parameters">
            <p
              style={{
                fontSize: typography.caption,
                fontWeight: 700,
                color: palette.text,
                margin: '0 0 2px 0'
              }}
            >
              Geometry Parameters
            </p>
            {dimensions.length === 0 ? (
              <p
                style={{
                  fontSize: typography.body,
                  color: palette.textMuted,
                  margin: 0
                }}
              >
                No geometry parameters captured.
              </p>
            ) : (
              <div style={{ border: HAIRLINE }}>
                {dimensions.map((d, idx) => (
                  <div
                    key={d.key}
                    style={{
                      display: 'flex',
                      padding: '1px 4px',
                      borderBottom: idx === dimensions.length - 1 ? 'none' : HAIRLINE
                    }}
                  >
                    <span
                      style={{
                        flex: 1,
                        fontSize: typography.caption,
                        color: palette.textMuted,
                        fontWeight: 700,
                        borderRight: HAIRLINE,
                        paddingRight: 4
                      }}
                    >
                      {d.label}
                    </span>
                    <span
                      style={{
                        width: 70,
                        fontSize: typography.body,
                        color: palette.text,
                        textAlign: 'right',
                        paddingLeft: 4
                      }}
                    >
                      {d.value}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <SeamsLocationBlock seamLocations={seamLocations} />
        </div>
      </SectionBody>
    </>
  )
}

function SeamsLocationBlock({
  seamLocations
}: {
  seamLocations?: { upstream: string; downstream: string }
}) {
  const upstream = (seamLocations?.upstream ?? '').trim() || '—'
  const downstream = (seamLocations?.downstream ?? '').trim() || '—'
  const headerStyle: React.CSSProperties = {
    fontSize: typography.caption,
    fontWeight: 700,
    backgroundColor: palette.surfaceMuted,
    padding: '2px 4px',
    borderBottom: HAIRLINE
  }
  const cellStyle: React.CSSProperties = {
    fontSize: typography.body,
    padding: '2px 4px',
    borderBottom: HAIRLINE,
    borderRight: HAIRLINE
  }
  return (
    <div
      data-testid="pqr-location-of-seams"
      style={{
        margin: '0 4px 4px',
        border: HAIRLINE
      }}
    >
      <div style={headerStyle}>Location of Seams:</div>
      <div style={{ display: 'flex' }}>
        <div style={{ ...cellStyle, width: '40%' }}>Upstream</div>
        <div style={{ ...cellStyle, flex: 1, borderRight: 'none' }}>{upstream}</div>
      </div>
      <div style={{ display: 'flex' }}>
        <div style={{ ...cellStyle, width: '40%', borderBottom: 'none' }}>Downstream</div>
        <div style={{ ...cellStyle, flex: 1, borderRight: 'none', borderBottom: 'none' }}>
          {downstream}
        </div>
      </div>
    </div>
  )
}

function JointPreparationPanel({
  data,
  isDirty,
  onSectionClick,
  fieldLinkContext,
  fieldLinkActions
}: {
  data: PqrPreviewTemplateData
  isDirty: (s: PqrSectionName) => boolean
  onSectionClick: (s: PqrSectionName) => void
  fieldLinkContext?: PqrFieldLinkContext
  fieldLinkActions?: PqrPreviewFieldLinkActions
}) {
  void isDirty
  void onSectionClick
  // Multi-weld: when `jointPreparations[]` is present (from
  // `mapPqrTemplateData` running with >1 scope), render one stacked
  // panel-body per weld so each weld's bevel image + dimensions show
  // up. Single-weld / legacy mode falls back to the single
  // `jointPreparation` field.
  const entries =
    Array.isArray(data.jointPreparations) && data.jointPreparations.length > 0
      ? data.jointPreparations
      : [data.jointPreparation]
  const bevelIndex = 0

  const renderBody = (entry: typeof entries[number], idx: number) => (
    <React.Fragment key={`${entry.weldId ?? 'jp'}-${idx}`}>
      <JointPreparationPanelBody
        jointType={entry.jointType}
        dimensions={entry.dimensions}
        imageUrl={entry.imageUrl}
        seamLocations={entry.seamLocations}
      />
    </React.Fragment>
  )

  const stacked = (
    <>
      {entries.map((entry, idx) => renderBody(entry, idx))}
    </>
  )

  if (fieldLinkContext) {
    const firstEntry = entries[0]
    return (
      <PqrPreviewFieldLink
        descriptor={{ section: 'joint-preparation', bevelIndex }}
        fieldLabel={
          firstEntry.jointType
            ? `Joint Preparation — ${firstEntry.jointType}`
            : 'Joint Preparation'
        }
        context={fieldLinkContext}
        actions={fieldLinkActions}
        dataAttributes={{
          'data-section': 'jointPreparation',
          'data-dirty': 'false'
        }}
      >
        {stacked}
      </PqrPreviewFieldLink>
    )
  }
  return (
    <div data-section="jointPreparation" data-dirty="false">
      {stacked}
    </div>
  )
}

function MidBand({
  data,
  isDirty,
  onSectionClick,
  fieldLinkContext,
  fieldLinkActions
}: {
  data: PqrPreviewTemplateData
  isDirty: (s: PqrSectionName) => boolean
  onSectionClick: (s: PqrSectionName) => void
  fieldLinkContext?: PqrFieldLinkContext
  fieldLinkActions?: PqrPreviewFieldLinkActions
}) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
      <div style={{ flexGrow: 1, flexBasis: 0 }}>
        <WeldingVariablesTable
          data={data}
          isDirty={isDirty}
          onSectionClick={onSectionClick}
          fieldLinkContext={fieldLinkContext}
          fieldLinkActions={fieldLinkActions}
        />
      </div>
      <div style={{ width: 240 }}>
        <JointPreparationPanel
          data={data}
          isDirty={isDirty}
          onSectionClick={onSectionClick}
          fieldLinkContext={fieldLinkContext}
          fieldLinkActions={fieldLinkActions}
        />
      </div>
    </div>
  )
}

// ----- Welding Parameters + Weld Pass Sequence (page 2) -----------------

/**
 * Top-row column header for the Welding Parameters table. Most columns
 * are decorative (no navigation), but the `preheat` column is special:
 * its data lives on WpsPreheatDetails, not in the per-weld telemetry,
 * so the header itself routes to /preheat (matching the per-cell
 * override in `WeldingParameterCell`). Without that, the user would
 * see a clickable column of cells but a dead header above them, which
 * read as inconsistent during QA.
 */
function WeldingParameterColumnHeader({
  columnKey,
  header,
  unit,
  fieldLinkContext,
  fieldLinkActions
}: {
  columnKey: string
  header: string
  unit: string
  fieldLinkContext?: PqrFieldLinkContext
  fieldLinkActions?: PqrPreviewFieldLinkActions
}) {
  const ctxArg = fieldLinkContext ?? {
    projectId: '',
    pipeSetId: '',
    weldId: null,
    syntheticWeldId: null,
    previewUrl: ''
  }
  const isPreheat = columnKey === 'preheat'
  const descriptor: PqrFieldDescriptor = {
    section: 'equipment-preheat',
    fieldKey: 'preheat'
  }
  const nav = usePqrFieldLinkNavigation(
    descriptor,
    `${header} column`,
    ctxArg,
    fieldLinkActions
  )
  const clickable = isPreheat && !!fieldLinkContext && !nav.disabled
  return (
    <>
      <th
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-label={clickable ? `Edit ${header} on Preheat Details` : undefined}
        onClick={clickable ? nav.handleClick : undefined}
        onKeyDown={clickable ? nav.handleKeyDown : undefined}
        style={{
          color: '#ffffff',
          padding: '2px 3px',
          borderRight: `0.5px solid ${palette.bannerDeep}`,
          textAlign: 'center',
          verticalAlign: 'middle',
          cursor: clickable ? 'pointer' : 'default'
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <span style={{ fontSize: typography.caption, fontWeight: 700 }}>
            {header}
          </span>
          <span
            style={{
              fontSize: typography.micro,
              fontStyle: 'italic',
              fontWeight: 400
            }}
          >
            ({unit})
          </span>
        </div>
      </th>
      {clickable ? (
        <PqrNavigateConfirmModal open={nav.open} {...nav.modalProps} />
      ) : null}
    </>
  )
}

function WeldingParameterCell({
  cell,
  passLabel,
  columnKey,
  columnHeader,
  fieldLinkContext,
  fieldLinkActions
}: {
  cell: PqrPreviewTemplateData['weldingParameters']['rows'][number]['cells'][number]
  passLabel: string
  columnKey: string
  columnHeader: string
  fieldLinkContext?: PqrFieldLinkContext
  fieldLinkActions?: PqrPreviewFieldLinkActions
}) {
  // Cells are only clickable when a field-link context is present. The
  // hook still runs unconditionally so we can read `nav.disabled` to
  // decide whether to wire click handlers.
  //
  // Per-column override: the entire `preheat` column doesn't live in
  // the per-weld telemetry stream \u2014 it's set on WpsPreheatDetails.
  // Route those cells (and the column header below) to /preheat so
  // the user can correct the source value instead of being dropped
  // into a per-weld telemetry view that doesn't carry the field.
  // Every other column still drills into the per-weld WeldParameters
  // page via the Slice-08 `welding-parameter-cell` descriptor.
  const ctxArg = fieldLinkContext ?? {
    projectId: '',
    pipeSetId: '',
    weldId: null,
    syntheticWeldId: null,
    previewUrl: ''
  }
  const descriptor: PqrFieldDescriptor =
    columnKey === 'preheat'
      ? { section: 'equipment-preheat', fieldKey: 'preheat' }
      : { section: 'welding-parameter-cell', passLabel, columnKey }
  const nav = usePqrFieldLinkNavigation(
    descriptor,
    `${columnHeader} — ${passLabel}`,
    ctxArg,
    fieldLinkActions
  )
  const clickable = !!fieldLinkContext && !nav.disabled
  return (
    <>
      <td
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-label={clickable ? `Edit ${columnHeader} for ${passLabel}` : undefined}
        onClick={clickable ? nav.handleClick : undefined}
        onKeyDown={clickable ? nav.handleKeyDown : undefined}
        style={{
          padding: 0,
          borderRight: HAIRLINE,
          cursor: clickable ? 'pointer' : 'default'
        }}
      >
        <div style={{ display: 'flex' }}>
          <span
            style={{
              flex: 1,
              padding: '2px 3px',
              textAlign: 'center',
              fontSize: typography.body,
              color: palette.text
            }}
          >
            {cell.min}
          </span>
          <span
            style={{
              flex: 1,
              padding: '2px 3px',
              textAlign: 'center',
              fontSize: typography.body,
              color: palette.text
            }}
          >
            {cell.max}
          </span>
        </div>
      </td>
      {clickable ? <PqrNavigateConfirmModal open={nav.open} {...nav.modalProps} /> : null}
    </>
  )
}

function WeldingParametersTable({
  data,
  isDirty,
  onSectionClick,
  fieldLinkContext,
  fieldLinkActions
}: {
  data: PqrPreviewTemplateData
  isDirty: (s: PqrSectionName) => boolean
  onSectionClick: (s: PqrSectionName) => void
  fieldLinkContext?: PqrFieldLinkContext
  fieldLinkActions?: PqrPreviewFieldLinkActions
}) {
  void isDirty
  void onSectionClick
  const { columns, rows } = data.weldingParameters
  return (
    <div
      data-section="weldingParameters"
      data-dirty="false"
      style={{ position: 'relative' }}
    >
      <SectionHeader title="Welding Parameters" />
      <SectionBody>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 80 }} />
            {columns.map((c) => (
              <col key={c.key} />
            ))}
          </colgroup>
          <thead>
            {/* Row 1 — column groups with unit subtitle */}
            <tr style={{ backgroundColor: palette.banner }}>
              <th
                style={{
                  color: '#ffffff',
                  fontSize: typography.caption,
                  fontWeight: 700,
                  padding: '2px 3px',
                  borderRight: `0.5px solid ${palette.bannerDeep}`,
                  textAlign: 'left'
                }}
              >
                Pass
              </th>
              {columns.map((col) => (
                <WeldingParameterColumnHeader
                  key={col.key}
                  columnKey={col.key}
                  header={col.header}
                  unit={col.unit}
                  fieldLinkContext={fieldLinkContext}
                  fieldLinkActions={fieldLinkActions}
                />
              ))}
            </tr>
            {/* Row 2 — Min/Max sub-headers on the deeper band */}
            <tr style={{ backgroundColor: palette.bannerDeep }}>
              <th
                style={{
                  backgroundColor: palette.bannerDeep,
                  borderRight: `0.5px solid ${palette.bannerDeep}`
                }}
              />
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: 0,
                    borderRight: `0.5px solid ${palette.bannerDeep}`
                  }}
                >
                  <div style={{ display: 'flex' }}>
                    <span
                      style={{
                        flex: 1,
                        color: '#ffffff',
                        fontSize: typography.caption,
                        fontWeight: 700,
                        padding: '2px 3px',
                        textAlign: 'center'
                      }}
                    >
                      Min
                    </span>
                    <span
                      style={{
                        flex: 1,
                        color: '#ffffff',
                        fontSize: typography.caption,
                        fontWeight: 700,
                        padding: '2px 3px',
                        textAlign: 'center'
                      }}
                    >
                      Max
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  style={{
                    padding: '4px 6px',
                    color: palette.textMuted,
                    fontSize: typography.body
                  }}
                >
                  No pass parameters captured.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.pass} style={{ borderBottom: HAIRLINE }}>
                  <th
                    scope="row"
                    style={{
                      padding: '2px 3px',
                      borderRight: HAIRLINE,
                      textAlign: 'left',
                      fontSize: typography.body,
                      fontWeight: 700,
                      color: palette.text
                    }}
                  >
                    {row.pass}
                  </th>
                  {row.cells.map((cell, idx) => {
                    const col = columns[idx]
                    return (
                      <WeldingParameterCell
                        key={idx}
                        cell={cell}
                        passLabel={row.pass}
                        columnKey={col?.key ?? ''}
                        columnHeader={col?.header ?? ''}
                        fieldLinkContext={fieldLinkContext}
                        fieldLinkActions={fieldLinkActions}
                      />
                    )
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </SectionBody>
    </div>
  )
}

function WeldPassSequencePanel({ data }: { data: PqrPreviewTemplateData }) {
  // Multi-weld: stack one panel-body per scope when
  // `weldPassSequences[]` is present. Single-weld / legacy falls back
  // to the single `weldPassSequence` field.
  const entries =
    Array.isArray(data.weldPassSequences) && data.weldPassSequences.length > 0
      ? data.weldPassSequences
      : [data.weldPassSequence]
  const buildLabel = (wps: typeof entries[number]) => {
    const parts: string[] = []
    if (wps.rootType) parts.push(`${wps.rootType[0].toUpperCase()}${wps.rootType.slice(1)} root`)
    if (wps.fillCount !== null) parts.push(`${wps.fillCount} fill`)
    if (wps.capCount !== null) parts.push(`${wps.capCount} cap`)
    return parts.join(' · ') || '—'
  }
  return (
    <div>
      <SectionHeader title="Weld Pass Sequence" />
      <SectionBody>
        {entries.map((wps, idx) => (
          <React.Fragment key={`${wps.weldId ?? 'wps'}-${idx}`}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 4,
                padding: '2px 4px',
                borderBottom: HAIRLINE_SOFT,
                backgroundColor: palette.surfaceMuted
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  fontSize: typography.caption,
                  color: palette.textMuted,
                  fontWeight: 700
                }}
              >
                Configuration:
              </span>
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: typography.body,
                  color: palette.text,
                  fontWeight: 700,
                  textAlign: 'right',
                  wordBreak: 'break-word'
                }}
              >
                {buildLabel(wps)}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 6,
                minHeight: 120
              }}
            >
              {wps.imageUrl ? (
                <img
                  src={wps.imageUrl}
                  alt="Weld pass sequence diagram"
                  style={{ width: 200, maxHeight: 200, objectFit: 'contain' }}
                />
              ) : (
                <p
                  style={{
                    fontSize: typography.caption,
                    color: palette.textMuted,
                    textAlign: 'center',
                    margin: 0
                  }}
                >
                  {wps.rootType && wps.capCount !== null && wps.fillCount !== null
                    ? 'No diagram uploaded for this configuration.'
                    : 'Set root type, fill, and cap counts on the Pass Details screen to surface the diagram.'}
                </p>
              )}
            </div>
          </React.Fragment>
        ))}
      </SectionBody>
    </div>
  )
}

function WeldingParametersBand({
  data,
  isDirty,
  onSectionClick,
  fieldLinkContext,
  fieldLinkActions
}: {
  data: PqrPreviewTemplateData
  isDirty: (s: PqrSectionName) => boolean
  onSectionClick: (s: PqrSectionName) => void
  fieldLinkContext?: PqrFieldLinkContext
  fieldLinkActions?: PqrPreviewFieldLinkActions
}) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
      <div style={{ flexGrow: 1, flexBasis: 0 }}>
        <WeldingParametersTable
          data={data}
          isDirty={isDirty}
          onSectionClick={onSectionClick}
          fieldLinkContext={fieldLinkContext}
          fieldLinkActions={fieldLinkActions}
        />
      </div>
      <div style={{ width: 240 }}>
        <WeldPassSequencePanel data={data} />
      </div>
    </div>
  )
}

// ----- Notes (single split-grid mirroring the PDF) ----------------------

/**
 * Discriminated row used by the Notes block. Slice 01 only makes
 * `kind === 'welder'` rows clickable navigation targets — equipment
 * rows render as plain rows (Slice 04 will make them navigable).
 */
type NotesRow =
  | { kind: 'welder'; label: string; value: string; equipmentKey: string | null }
  | { kind: 'equipment'; label: string; value: string }
  | { kind: 'spacer' }
  | { kind: 'group-header'; label: string }

function NotesBlock({
  data,
  isDirty,
  onSectionClick,
  fieldLinkContext,
  fieldLinkActions
}: {
  data: PqrPreviewTemplateData
  isDirty: (s: PqrSectionName) => boolean
  onSectionClick: (s: PqrSectionName) => void
  fieldLinkContext?: PqrFieldLinkContext
  fieldLinkActions?: PqrPreviewFieldLinkActions
}) {
  // Notes no longer has its own staging slot (Slice 04 of
  // pqr-preview-field-navigation deleted it). The section-level
  // EditTarget is gone; each row navigates to its own source page.
  void onSectionClick

  // Combine welder rows + equipment rows into a single ordered list, then
  // split it in half so both columns end up with the same number of rows.
  // Mirrors `NotesBlock` in `server/src/pdf/PqrTemplate.tsx`.
  //
  // Multi-weld: when `data.weldersByWeld` is populated (i.e. the user
  // picked >1 weld), the welder portion is rendered as one labelled
  // group per weld instead of the deduplicated flat list. Equipment
  // stays global (it's a WPS-level concept).
  //
  // Notes third-slot role comes from the mapper (rootEquipment / pass list).
  // Trust the role labels already on `notes.welders` / `weldersByWeld`.
  const groupedWelders = data.weldersByWeld
  const welderRows: NotesRow[] =
    Array.isArray(groupedWelders) && groupedWelders.length > 1
      ? groupedWelders.flatMap((group, groupIdx): NotesRow[] => [
          ...(groupIdx > 0 ? [{ kind: 'spacer' as const }] : []),
          ...group.rows.map<NotesRow>((w) => ({
            kind: 'welder',
            label: w.role,
            value: w.name,
            equipmentKey: deriveEquipmentKeyFromWelderRole(w.role)
          }))
        ])
      : data.notes.welders.map<NotesRow>((w) => ({
          kind: 'welder',
          label: w.role,
          value: w.name,
          equipmentKey: deriveEquipmentKeyFromWelderRole(w.role)
        }))

  const allRows: NotesRow[] = [
    ...welderRows,
    ...data.notes.equipment.map<NotesRow>((e) => ({
      kind: 'equipment',
      label: e.label,
      value: e.value
    }))
  ]

  void isDirty
  return (
    <div data-section="notes" style={{ marginBottom: 4 }}>
      <SectionHeader title="Notes" />
      {allRows.length === 0 ? (
        <SectionBody>
          <div style={{ padding: 6 }}>
            <span style={{ fontSize: typography.body, color: palette.textMuted }}>—</span>
          </div>
        </SectionBody>
      ) : (
        <div
          style={{
            border: BORDER,
            borderTop: 'none',
            display: 'flex'
          }}
        >
          {(() => {
            const half = Math.ceil(allRows.length / 2)
            const leftRows = allRows.slice(0, half)
            const rightRows = allRows.slice(half)
            while (rightRows.length < leftRows.length) {
              rightRows.push({ kind: 'spacer' })
            }
            return (
              <>
                <NotesColumn
                  rows={leftRows}
                  bordered
                  fieldLinkContext={fieldLinkContext}
                  fieldLinkActions={fieldLinkActions}
                />
                <NotesColumn
                  rows={rightRows}
                  fieldLinkContext={fieldLinkContext}
                  fieldLinkActions={fieldLinkActions}
                />
              </>
            )
          })()}
        </div>
      )}
    </div>
  )
}

function NotesColumn({
  rows,
  bordered,
  fieldLinkContext,
  fieldLinkActions
}: {
  rows: NotesRow[]
  bordered?: boolean
  fieldLinkContext?: PqrFieldLinkContext
  fieldLinkActions?: PqrPreviewFieldLinkActions
}) {
  return (
    <div
      style={{
        flex: 1,
        flexBasis: 0,
        flexGrow: 1,
        borderRight: bordered ? HAIRLINE : 'none'
      }}
    >
      {rows.map((row, idx) => {
        const isSpacer = row.kind === 'spacer'
        const isHeader = row.kind === 'group-header'
        const isLastRow = idx === rows.length - 1
        if (isHeader) {
          return (
            <div
              key={`hdr-${idx}`}
              style={{
                fontSize: typography.caption,
                fontWeight: 700,
                color: palette.text,
                backgroundColor: palette.surfaceMuted,
                padding: '2px 4px',
                borderBottom: HAIRLINE
              }}
            >
              {row.label}
            </div>
          )
        }
        const label = isSpacer ? '' : (row as { label: string }).label
        const value = isSpacer
          ? ''
          : (row as { value?: string }).value ?? ''
        const lines = String(value || '').split('\n')

        const labelCell = (
          <span
            style={{
              width: 130,
              fontSize: typography.caption,
              color: palette.text,
              fontWeight: 700,
              backgroundColor: isSpacer ? 'transparent' : palette.surfaceMuted,
              padding: '2px 4px',
              borderRight: HAIRLINE,
              cursor: 'inherit'
            }}
          >
            {label || '\u00A0'}
          </span>
        )

        const valueCell = (
          <span
            style={{
              flex: 1,
              fontSize: typography.body,
              color: palette.text,
              padding: '2px 4px',
              whiteSpace: 'pre-wrap'
            }}
          >
            {isSpacer ? '\u00A0' : lines.join('\n')}
          </span>
        )

        // Wrap welder rows in a PqrPreviewFieldLink when a context is
        // available AND the role maps to a known operator field key.
        // The link spans the whole row so users can click anywhere on it,
        // matching the per-row granularity decision from grilling Q3.
        if (
          row.kind === 'welder' &&
          fieldLinkContext &&
          row.equipmentKey !== null
        ) {
          return (
            <PqrPreviewFieldLink
              key={idx}
              descriptor={{ section: 'notes-welder', equipmentKey: row.equipmentKey }}
              fieldLabel={row.label}
              context={fieldLinkContext}
              actions={fieldLinkActions}
              style={{
                display: 'flex',
                borderBottom: isLastRow ? 'none' : HAIRLINE,
                minHeight: 14
              }}
            >
              {labelCell}
              {valueCell}
            </PqrPreviewFieldLink>
          )
        }

        // Equipment rows route to the WPS source page that owns the
        // data. Slice 09 of pqr-preview-field-navigation introduced a
        // label-driven classifier so each equipment row (Welding Power
        // Supplies, Clamp Release, Preheat Method, Cleaning / Grinding,
        // Root External Treatment, ...) lands on the right form with a
        // focus marker. Labels outside the known set keep the legacy
        // notes-equipment route to the project edit page. All are
        // pipe-set-wide, so they remain clickable in aggregated mode.
        if (row.kind === 'equipment' && fieldLinkContext) {
          const classified = classifyEquipmentNoteLabel(row.label)
          const descriptor = classified ?? { section: 'notes-equipment' as const }
          return (
            <PqrPreviewFieldLink
              key={idx}
              descriptor={descriptor}
              fieldLabel={row.label}
              context={fieldLinkContext}
              actions={fieldLinkActions}
              style={{
                display: 'flex',
                borderBottom: isLastRow ? 'none' : HAIRLINE,
                minHeight: 14
              }}
            >
              {labelCell}
              {valueCell}
            </PqrPreviewFieldLink>
          )
        }

        return (
          <div
            key={idx}
            style={{
              display: 'flex',
              borderBottom: isLastRow ? 'none' : HAIRLINE,
              minHeight: 14
            }}
          >
            {labelCell}
            {valueCell}
          </div>
        )
      })}
    </div>
  )
}

// ----- Weld Completion Times (full-width row) ---------------------------

function WeldCompletionTimesRow({
  data,
  fieldLinkContext,
  fieldLinkActions
}: {
  data: PqrPreviewTemplateData
  fieldLinkContext?: PqrFieldLinkContext
  fieldLinkActions?: PqrPreviewFieldLinkActions
}) {
  const slots =
    Array.isArray(data.notes.weldCompletionTimes) && data.notes.weldCompletionTimes.length > 0
      ? data.notes.weldCompletionTimes
      : [
          { label: 'Root Complete', value: '—' },
          { label: 'Hot Pass Start', value: '—' },
          { label: 'Weld Completed', value: '—' }
        ]

  return (
    <div data-section="weld-completion-times" style={{ marginBottom: 4 }}>
      <SectionHeader title="Weld Completion Times" />
      <div
        style={{
          border: BORDER,
          borderTop: 'none',
          display: 'flex'
        }}
      >
        {slots.map((slot, idx) => {
          const descriptor = classifyEquipmentNoteLabel(slot.label)
          const inner = (
            <>
              <span
                style={{
                  width: 100,
                  fontSize: typography.caption,
                  color: palette.text,
                  fontWeight: 700,
                  backgroundColor: palette.surfaceMuted,
                  padding: '2px 4px',
                  borderRight: HAIRLINE
                }}
              >
                {slot.label}
              </span>
              <span
                style={{
                  flex: 1,
                  fontSize: typography.body,
                  color: palette.text,
                  padding: '2px 4px'
                }}
              >
                {slot.value || '—'}
              </span>
            </>
          )
          const cellStyle: React.CSSProperties = {
            flex: 1,
            flexBasis: 0,
            display: 'flex',
            borderRight: idx === slots.length - 1 ? 'none' : HAIRLINE,
            minHeight: 16
          }
          if (descriptor && fieldLinkContext) {
            return (
              <PqrPreviewFieldLink
                key={`${slot.label}-${idx}`}
                descriptor={descriptor}
                fieldLabel={slot.label}
                context={fieldLinkContext}
                actions={fieldLinkActions}
                style={cellStyle}
              >
                {inner}
              </PqrPreviewFieldLink>
            )
          }
          return (
            <div key={`${slot.label}-${idx}`} style={cellStyle}>
              {inner}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ----- Accompanying Reports + Comments (stacked) ----------------------

function AccompanyingReports({ data }: { data: PqrPreviewTemplateData }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <SectionHeader title="Accompanying Reports" />
      <SectionBody>
        <div style={{ padding: 6 }}>
          {data.accompanyingReports.length === 0 ? (
            <span style={{ fontSize: typography.body, color: palette.textMuted }}>—</span>
          ) : (
            data.accompanyingReports.map((r, idx) => (
              <div
                key={`${r.label}-${idx}`}
                style={{
                  display: 'flex',
                  borderBottom: HAIRLINE,
                  minHeight: 14
                }}
              >
                <span
                  style={{
                    width: 130,
                    fontSize: typography.caption,
                    color: palette.text,
                    fontWeight: 700,
                    backgroundColor: palette.surfaceMuted,
                    padding: '2px 4px',
                    borderRight: HAIRLINE
                  }}
                >
                  {r.label}:
                </span>
                <span
                  style={{
                    flex: 1,
                    fontSize: typography.body,
                    color: palette.text,
                    padding: '2px 4px'
                  }}
                >
                  {r.value}
                </span>
              </div>
            ))
          )}
        </div>
      </SectionBody>
    </div>
  )
}

function CommentsBlock({
  data,
  fieldLinkContext,
  fieldLinkActions
}: {
  data: PqrPreviewTemplateData
  fieldLinkContext?: PqrFieldLinkContext
  fieldLinkActions?: PqrPreviewFieldLinkActions
}) {
  const body = (
    <div
      style={{
        border: BORDER,
        borderTop: 'none',
        padding: 6,
        minHeight: 40,
        fontSize: typography.body,
        color: palette.text,
        whiteSpace: 'pre-wrap'
      }}
    >
      {data.comments ? (
        data.comments
      ) : (
        <span style={{ color: palette.textMuted }}>—</span>
      )}
    </div>
  )

  return (
    <div style={{ marginBottom: 4 }}>
      <SectionHeader title="Comments" />
      {fieldLinkContext ? (
        <PqrPreviewFieldLink
          descriptor={{ section: 'comments-wpsform' }}
          fieldLabel="Comments (WPS form)"
          context={fieldLinkContext}
          actions={fieldLinkActions}
        >
          {body}
        </PqrPreviewFieldLink>
      ) : (
        body
      )}
    </div>
  )
}

// ----- Page footer (fixed in PDF — bottom-aligned here) -----------------

function PageFooter({
  project,
  generatedAt
}: {
  project: ProjectIdentitySummary
  generatedAt: string
}) {
  return (
    <div
      style={{
        borderTop: HAIRLINE,
        paddingTop: 3,
        marginTop: 'auto',
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: typography.micro,
        color: palette.textSubtle,
        textTransform: 'uppercase',
        letterSpacing: '0.4px'
      }}
    >
      <span>Project: {project.projectName || '—'}</span>
      <span>Generated on: {formatGeneratedAt(generatedAt)} UTC</span>
    </div>
  )
}

// ----- A3 portrait page shell -------------------------------------------

/**
 * Single A3 PORTRAIT page wrapper. The PDF uses 842 × 1191 pt (~29.7 × 42
 * cm); we mirror the aspect ratio so the rendered preview lays out cells
 * the same way as the PDF. Dimensions come from `canvasFrame` in
 * `./pqrTheme.ts` so future page-size changes live in one place.
 */
function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        width: '100%',
        maxWidth: canvasFrame.maxWidth,
        aspectRatio: canvasFrame.aspectRatio,
        margin: '0 auto',
        backgroundColor: palette.surface,
        border: BORDER,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        ...baseText
      }}
    >
      {children}
    </div>
  )
}

// ----- top-level canvas --------------------------------------------------

export default function PqrPreviewCanvas({
  data,
  project,
  isDirty,
  onSectionClick,
  fieldLinkContext,
  fieldLinkActions
}: Props) {
  // SINGLE A3 PORTRAIT page — mirrors `server/src/pdf/PqrTemplate.tsx` after
  // the A3 portrait switch. Every section now renders in one shell in the
  // same top-to-bottom order the PDF produces. The legacy "Page 2 of 2"
  // mid-render split is gone because the PDF no longer breaks pages.
  return (
    <div data-testid="pqr-preview-canvas">
      <PageShell>
        <TopBand
          data={data}
          project={project}
          isDirty={isDirty}
          onSectionClick={onSectionClick}
        />
        <CompanyBlock data={data} />
        <MaterialTable
          data={data}
          isDirty={isDirty}
          onSectionClick={onSectionClick}
          fieldLinkContext={fieldLinkContext}
          fieldLinkActions={fieldLinkActions}
        />
        <MidBand
          data={data}
          isDirty={isDirty}
          onSectionClick={onSectionClick}
          fieldLinkContext={fieldLinkContext}
          fieldLinkActions={fieldLinkActions}
        />
        <WeldingParametersBand
          data={data}
          isDirty={isDirty}
          onSectionClick={onSectionClick}
          fieldLinkContext={fieldLinkContext}
          fieldLinkActions={fieldLinkActions}
        />
        <NotesBlock
          data={data}
          isDirty={isDirty}
          onSectionClick={onSectionClick}
          fieldLinkContext={fieldLinkContext}
          fieldLinkActions={fieldLinkActions}
        />
        <WeldCompletionTimesRow
          data={data}
          fieldLinkContext={fieldLinkContext}
          fieldLinkActions={fieldLinkActions}
        />
        <AccompanyingReports data={data} />
        <CommentsBlock
          data={data}
          fieldLinkContext={fieldLinkContext}
          fieldLinkActions={fieldLinkActions}
        />
        <div style={{ flexGrow: 1 }} />
        <PageFooter project={project} generatedAt={data.generatedAt} />
      </PageShell>
    </div>
  )
}
